// pfs-card.ts — die Card-Hülle als Primitive (PFS-652 Spacing-System, PFS-756).
//
// SSOT für die Card-STRUKTUR: die Card vereint Header + Body, also besitzt SIE
// die Sektionierung — nicht der Header, nicht der Body. Sie trägt die Optik
// (Surface/Border/Radius/Shadow) + das Inset der Body-Region + die Struktur
// (optionales Header-Band via slot="header").
//
//   Flach (Default, rückwärtskompatibel): eine Zone, Inset rundum, Kinder via
//     Section-Gap gestapelt — bestehende Cards funktionieren unverändert.
//     <pfs-card>
//       <pfs-card-header …></pfs-card-header>
//       <pfs-field-group …> … </pfs-field-group>
//     </pfs-card>
//
//   Sektioniert (data-sectioned): Header-Band · Divider · Body-Zone. Die CARD
//     entscheidet die Struktur — ein <pfs-card-header>-Kind wandert AUTOMATISCH
//     ins Header-Band (voll-breite Trennlinie darunter), der Body idealerweise
//     in <pfs-card-body>. Kein slot="header" nötig; Header/Body tragen den
//     Divider NICHT (der hängt an data-sectioned an der Card).
//     <pfs-card data-sectioned>
//       <pfs-card-header …></pfs-card-header>
//       <pfs-card-body> … </pfs-card-body>
//     </pfs-card>
//
// Die Migration flach → sektioniert (MFA-Optik) läuft als Epic pro Env/Repo;
// bis dahin bleibt die flache Form live. Inset via --pfs-space-inset (UNIFORM,
// PFS-659), Rhythmik via --pfs-space-section — beide am :root überschreibbar.
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";

const STYLES = `
:host {
  display: block;
  background: var(--pfs-color-surface);
  border: 1px solid var(--pfs-color-outline);
  border-radius: var(--pfs-radius-md, 0.5rem);
  box-shadow: var(--pfs-shadow-card, 0 1px 2px rgba(16, 33, 43, 0.06));
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}
/* Header-Band-Region: nur sichtbar, wenn slot="header" befüllt ist. */
.header { display: none; }
.header.filled { display: block; padding: var(--pfs-space-inset, 1rem); }
/* Sektioniert: voll-breite Trennlinie zur Body-Zone. */
:host([data-sectioned]) .header.filled { border-bottom: 1px solid var(--pfs-color-outline); }
/* Body-Region: Inset + Section-Rhythmik (rückwärtskompatibel — trägt Inhalt
   direkt ODER ein <pfs-card-body>, das nur die Rhythmik verfeinert). */
.body { display: flex; flex-direction: column; gap: var(--pfs-space-section, 1rem); padding: var(--pfs-space-inset, 1rem); }
`;

defineComponent({
  tagName: "pfs-card",
  eager: true,
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;

    const header = document.createElement("div");
    header.className = "header";
    header.innerHTML = `<slot name="header"></slot>`;
    const hslot = header.querySelector("slot") as HTMLSlotElement;
    // Header-Band nur zeigen, wenn slot="header" befüllt ist — sonst bleibt die
    // Card flach (Header liegt dann als Kind in der Body-Region).
    const syncHeader = () => {
      header.classList.toggle("filled", hslot.assignedNodes({ flatten: true }).length > 0);
    };
    hslot.addEventListener("slotchange", syncHeader);
    // syncHeader() NICHT hier (vor root.append) — der Slot ist noch nicht im
    // Shadow-DOM, assignedNodes()=leer → Band startet versteckt und poppt per
    // async slotchange nach (F1 Header-Jitter). Erst nach dem Append syncen.

    const body = document.createElement("div");
    body.className = "body";
    body.innerHTML = `<slot></slot>`;

    // Auto-Sektionierung (PFS-756): die Card besitzt die Struktur. Bei
    // data-sectioned wandert ein <pfs-card-header>-Kind selbsttätig ins Band
    // (slot="header") — der Consumer muss den Slot NICHT setzen. Flach: der
    // Header bleibt in der Body-Region (rückwärtskompatibel). Ein explizit
    // gesetztes slot="header" bleibt idempotent gültig.
    const assignHeaderSlot = () => {
      const sectioned = host.hasAttribute("data-sectioned");
      for (const child of Array.from(host.children)) {
        if (child.tagName.toLowerCase() !== "pfs-card-header") continue;
        if (sectioned) {
          if (child.getAttribute("slot") !== "header") child.setAttribute("slot", "header");
        } else if (child.getAttribute("slot") === "header") {
          child.removeAttribute("slot");
        }
      }
    };
    assignHeaderSlot();
    const obs = new MutationObserver(assignHeaderSlot);
    obs.observe(host, {
      childList: true,
      attributes: true,
      attributeFilter: ["data-sectioned"],
    });
    ctx.cleanup.push(() => obs.disconnect());

    root.append(style, header, body);
    // Jetzt sind die Slots im Shadow-DOM + der Header (falls sektioniert) via
    // assignHeaderSlot() bereits slot="header" → die Zuweisung ist synchron
    // korrekt: das Band (.filled) wird beim ersten Paint gesetzt, kein Jitter.
    syncHeader();
  },
});
